import { useForm } from "antd/es/form/Form";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../common/utils/useProjectNavigate";
import { Form, Modal, message } from "antd";
import { useTranslation } from "react-i18next";
import {
  fetchUsers,
  setIsOpenProfile,
  setProfileUser,
  selectIsOpenProfile,
  selectProfileUser,
  selectProfilePlacement,
  setProfilePlacement,
} from "../../store/slices/user_management";
import { selectUser, setUser } from "../../store/slices/user";
import useNotification from "../../components/notification/notification";
import ApiUser from "../../api/user";
import { Drawer } from "../drawer/drawer";
import { DrawerHeader } from "../drawer/drawer-header";
import { Icon } from "../icon/icon";
import { StopOutlined, CheckOutlined } from "@ant-design/icons";
import {
  FormAvatar,
  FormHeader,
  FormInput,
  FormInputButton,
  FormSelect,
  FormUserStatus,
} from "../form/form";
import ChangePasswordForm from "./forms/change-password-form";
import getDateByDiff from "../../common/utils/calendar/get-date-by-diff";
import getDate from "../../common/utils/calendar/get-date";
import ApiUsersManagement from "../../api/user-manager";
import UserAccessForm from "../../components/modals/invitations/user/user-access-form"; // ⬅️ додали
import { useProjectSites } from "../../components/modals/invitations/user/use-project-sites";
import { useCan } from "../../common/utils/usePermissions";
import {
  coversWholeProject,
  rowPayload,
  rowRoleOf,
  rowRoleOfLink,
  useAssignableRoles,
} from "../../components/modals/invitations/user/role-options";

const DeleteModalComponent = ({
  item,
  handleOk,
  handleCancel,
  isModalOpen,
}) => {
  return (
    <Modal
      title={`${item.firstName} ${item.lastName}`}
      open={isModalOpen}
      onOk={() => {
        handleOk(item.id, item);
      }}
      onCancel={handleCancel}
      okButtonProps={{ danger: true }}
      okText="Delete"
      className="plan-page-action-modal"
    >
      <p>Are you sure you want to delete this user?</p>
    </Modal>
  );
};

const Profile = () => {
  const user = useSelector(selectProfileUser);
  const currentUser = useSelector(selectUser);
  const isOpenProfile = useSelector(selectIsOpenProfile);
  const placement = useSelector(selectProfilePlacement);
  const [form] = useForm();
  const [isEdit, setEdit] = useState(false);
  const [isOpenPassword, setOpenPassword] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isDataLoaded = () => !Object.keys(user).length;
  const { hash } = useLocation();
  const [userRole, setUserRole] = useState();
  const [newUserAvatar, setNewUserAvatar] = useState();
  const { openNotification } = useNotification();
  const [isOpenDeleteModal, setOpenDeleteModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const navigate = useProjectNavigate();
  const location = useLocation();

  const isProjectScope = location.pathname.includes("/administration/project-users");
  const accessScope = isProjectScope ? "project" : "organization";
  const currentProject = useSelector((state) => state.projects.current);
  const holdsUsersEdit = useCan("users.edit");
  const holdsUsersStatus = useCan("users.status");
  const holdsUsersDelete = useCan("users.delete");

  const projects = useSelector((state) => state.projects.list || []);

  const [projectRows, setProjectRows] = useState([]);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [projectRoleDropdownKey, setProjectRoleDropdownKey] = useState(null);

  // LF360-2038: each row's sites come from its own project.
  const sitesFor = useProjectSites(
    projectRows.map((r) => r.projectId),
    isOpenProfile
  );

  /**
   * The sites a row saves. Left out where its project has no sites (or they
   * are not loaded yet), which keeps what is stored; a Project admin is on no
   * sites.
   */
  const siteIdsToSave = (row) => {
    if (!sitesFor(row.projectId).sitesEnabled) return undefined;
    return coversWholeProject(row) ? [] : row.siteIds || [];
  };

  // The rows as the user has them, before any edit.
  const storedProjectRows = () => {
    // On the project users page the user is edited in this project only.
    const links = (user.UserProjects || user.Projects || []).filter(
      (link) => accessScope !== "project" || link.projectId === currentProject?.id
    );
    let rows = links.map((link) => ({
      key: link.id || `${link.projectId}-${link.roleId || link.role}`,
      projectId: link.projectId,
      // LF360-2024: the role record, as the server names it.
      ...rowRoleOfLink(link),
      siteIds: Array.isArray(link.siteIds)
        ? link.siteIds
        : (link.Sites || []).map((s) => s.id),
    }));

    if (accessScope === "project") {
      const pid = currentProject?.id;
      if (pid && !rows.some((r) => r.projectId === pid)) {
        rows = [
          ...rows,
          {
            key: `new-${pid}`,
            projectId: pid,
            ...rowRoleOf(null),
            siteIds: [],
          }
        ];
      }
    }

    return rows;
  };

  useEffect(() => {
    if (!user) return;
    setProjectRows(storedProjectRows());
    // storedProjectRows reads exactly these.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, accessScope, currentProject]);

  // Bumped to remount the form, so inputs that keep what was typed in them
  // (they take defaultValue) show the user as stored again.
  const [formKey, setFormKey] = useState(0);

  /**
   * LF360-2038: leaving edit without saving puts everything back. Close used to
   * reset only the antd form, so the projects, sites, picked role and avatar
   * kept the unsaved edit on screen — and the picked organization role even
   * outlived the drawer, into the next user opened.
   */
  const discardEdits = () => {
    form.resetFields();
    setHasChanges(false);
    setUserRole(undefined);
    setNewUserAvatar(undefined);
    setProjectRoleDropdownKey(null);
    setProjectModalOpen(false);
    if (user) setProjectRows(storedProjectRows());
    setFormKey((key) => key + 1);
  };

  // LF360-2024: the roles the signed-in user may give, custom ones included.
  const roles = useAssignableRoles(isOpenProfile && isEdit);

  const selectedProjectIds = projectRows
    .map((r) => r.projectId)
    .filter(Boolean);

  const getProjectTitle = (id) =>
    (projects || []).find((p) => p.id === id)?.title || "—";

  const updateProjectRow = (key, patch) => {
    setProjectRows((rows) =>
      rows.map((row) =>
        row.key === key
          ? {
              ...row,
              ...patch,
              ...(coversWholeProject({ ...row, ...patch }) ? { siteIds: [] } : {}),
            }
          : row
      )
    );
    setHasChanges(true);
  };

  const handleSelectProject = (project) => {
    if (!project || !project.id) return;

    setProjectRows((rows) => {
      if (rows.some((r) => r.projectId === project.id)) {
        return rows;
      }
      return [
        ...rows,
        {
          key: Date.now().toString() + Math.random().toString(16).slice(2),
          projectId: project.id,
          ...rowRoleOf(null),
          siteIds: [],
        },
      ];
    });
    setHasChanges(true);
  };

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const roleOptions = [
    {
      label: "System admin",
      value: "systemAdmin",
    },
    {
      label: "Organization admin",
      value: "admin",
    },
    // Site manager is a project role, given per project below.
    {
      label: "User",
      value: "user",
    },
  ];

  const fetchUsersList = () => {
    let status;
    switch (hash) {
      case "#disabled":
        status = "inactive";
        break;
      case "#pending":
        status = ["invited", "inviteError"];
        break;
      default:
        break;
    }

    if (accessScope === "project" && currentProject?.id) {
      dispatch(
        fetchUsers({
          status,
          scope: "project",
          projectId: currentProject.id,
        })
      );
    } else {
      dispatch(
        fetchUsers({
          status,
          scope: "organization",
        })
      );
    }
  };

  const updateUser = () => {
    const fields = form.getFieldsValue();
    const newPath = location.pathname.replace(/\/edit$/, "");
    handleNavigate(newPath);
    if (!hasChanges) {
      setEdit(false);
      return;
    }

    const params = {};

    if (userRole) {
      fields.role = userRole;
    }

    if (newUserAvatar) {
      fields.avatar = newUserAvatar;
    }

    Object.keys(fields).forEach((key) => {
      if (fields[key]) {
        params[key] = fields[key];
      }
    });

    const orgRole = params.role || user.role;
    const isOrgUserRole = orgRole === "user";

    if (isOrgUserRole) {
      params.projects = projectRows
        .filter((r) => r.projectId && r.roleId)
        .map((r) => rowPayload({ ...r, siteIds: siteIdsToSave(r) }));

      // LF360-2038: the organization page lists every project the user has,
      // so one left out was removed — in any project, not only the current
      // one. The project users page sends no scope and keeps the rest.
      if (accessScope === "organization") {
        params.scope = "organization";
      }
    }

    ApiUser.update(user.id, params)
      .then((r) => {
        setEdit(false);
        dispatch(setProfileUser(r.data.user));
        fetchUsersList();
        if (user.id === currentUser.id) {
          dispatch(setUser({ ...currentUser, ...params }));
        }
      })
      .then(() => {
        message.success(t("profile.notification.update.success"));
        setHasChanges(false);
      })
      .catch((e) => {
        const code = e?.response?.data?.error?.code;
        message.error(
          t([`profile.notification.update.${code}`, "profile.notification.update.error"])
        );
      });
  };

  const reInvite = () => {
    const { id, email } = user;
    ApiUsersManagement.resendInvite(id)
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>{t("profile.notification.reinvite.success", { email })}</p>
          ),
        });
      })
      .catch(() => {
        openNotification({
          type: "error",
          message: <p>{t("profile.notification.reinvite.error")}</p>,
        });
      });
  };

  const isCurrentUser = () => user && user.id === currentUser.id;

  const isDisabledUser = () => ["blocked", "inactive"].includes(user.status);

  const shouldShowToogleStatusButton = () =>
    user && ["confirmed", "blocked", "inactive"].includes(user.status);

  const showReInviteButton = () =>
    user && ["invited", "inviteError"].includes(user.status);

  const toogleUserStatus = () => {
    const { activate, deactivate } = ApiUser;
    const disabledNow = isDisabledUser();
    const toogle = disabledNow ? activate(user.id) : deactivate(user.id);
    toogle
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t(
                `profile.notification.toogle_user.${
                  disabledNow ? "activated" : "deactivated"
                }`,
                {
                  name: user.fullName || `${user.firstName} ${user.lastName}`,
                }
              )}
            </p>
          ),
        });
      })
      .catch(() => {
        openNotification({
          type: "error",
          message: <p>{t("profile.notification.toogle_user.error")}</p>,
        });
      })
      .finally(() => {
        fetchUsersList();
        navigate("/administration/system-users");
      });
  };

  const onAvatarChange = (avatarUrl) => {
    // LF360-2038: UploadAvatar reports the image it starts with, on every
    // render of the edit form. Taken as a change, it made every Save send
    // something (and the stored avatar's signed URL as the new avatar), so a
    // save with nothing in it was never skipped. Only a different image counts.
    if ((avatarUrl || null) === (user?.avatarUrl || null)) return;
    setNewUserAvatar(avatarUrl);
    setHasChanges(true);
  };

  const onRoleChange = (id) => {
    setHasChanges(true);
    setUserRole(id);
  };

  const editToogle = () => {
    discardEdits();
    setEdit((prev) => !prev);

    let newPath;

    // if (!isEdit) {
    //   newPath = `/administration/system-users/${user.id}/edit`;
    // } else {
    //   newPath = location.pathname.replace(/\/edit$/, "");
    // }

    if (newPath) {
      handleNavigate(newPath);
    }
  };

  const openPasswordModal = () => setOpenPassword(true);
  const closePasswordModal = () => setOpenPassword(false);
  const openUserDeleteModal = () => setOpenDeleteModal(true);
  const closeDeleteUserModal = () => setOpenDeleteModal(false);

  const close = () => {
    setEdit(false);
    setOpenDeleteModal(false);
    discardEdits();
    dispatch(setIsOpenProfile(false));
    dispatch(setProfileUser());
    dispatch(setProfilePlacement("right"));
  };

  const deleteUser = () => {
    const { id } = user;
    ApiUsersManagement.deleteUser(id)
      .then(() => {
        openNotification({
          type: "success",
          message: <p>{t("profile.notification.delete.success")}</p>,
        });

        fetchUsersList();
        setOpenDeleteModal(false);
        close();
      })
      .catch((e) => {
        console.log(e);
        openNotification({
          type: "error",
          message: <p>{t("profile.notification.delete.error")}</p>,
        });
      });
  };

  const onFieldsChange = () => {
    if (!hasChanges) {
      setHasChanges(true);
    }
  };

  const isSystemAdmin = currentUser.role === "systemAdmin";
  const isTargetSystemAdmin = user && user.role === "systemAdmin";

  // LF360-2024: one's own profile stays editable; anybody else's needs the
  // permission the server checks for the action.
  const holdsEdit = isCurrentUser() || holdsUsersEdit;

  const canEditUser = isEdit && holdsEdit && (isSystemAdmin || !isTargetSystemAdmin);


  const canManageUser = !isCurrentUser() && holdsUsersStatus && (isSystemAdmin || !isTargetSystemAdmin);
  const canDeleteUser = !isCurrentUser() && holdsUsersDelete && (isSystemAdmin || !isTargetSystemAdmin);

  const canRemoveFromProject =
    accessScope === "project" && !!currentProject?.id;

  const canShowEditButton = holdsEdit && (isSystemAdmin || !isTargetSystemAdmin);
  const removeProjectRow = (key) => {
    setProjectRows((rows) => rows.filter((r) => r.key !== key));
    // LF360-2038: a removed project is a change to save. Without this, Save
    // after only a removal left edit mode without sending anything.
    setHasChanges(true);
  };

  const removeFromCurrentProject = () => {
    if (!currentProject?.id || !user) return;

    const remainingRows = projectRows.filter(
      (r) => r.projectId !== currentProject.id
    );

    const projectsPayload = remainingRows
      .filter((r) => r.projectId && r.roleId)
      .map((r) => rowPayload({ ...r, siteIds: siteIdsToSave(r) }));

    ApiUser.update(user.id, {
      projects: projectsPayload,
    })
      .then((r) => {
        setProjectRows(remainingRows);
        setHasChanges(false);
        dispatch(setProfileUser(r.data.user));
        fetchUsersList();

        openNotification({
          type: "success",
          message: <p>{t("profile.notification.remove_from_project.success")}</p>,
        });
      })
      .catch((e) => {
        console.log(e);
        openNotification({
          type: "error",
          message: <p>{t("profile.notification.remove_from_project.error")}</p>,
        });
      });
  };

  let headerMenu = undefined;

  if (user) {
    const items = [];

    if (showReInviteButton() && canManageUser) {
      items.push({
        label: t("profile.form.reinvite"),
        onClick: reInvite,
      });
    }

    // Activate / Deactivate
    if (shouldShowToogleStatusButton() && canManageUser) {
      items.push({
        label: t(
          `profile.form.${
            isDisabledUser() ? "activate_user" : "deactivate_user"
          }`
        ),
        icon: isDisabledUser() ? (
          <CheckOutlined style={{ marginRight: 8, marginLeft: -4 }} />
        ) : (
          <StopOutlined style={{ marginRight: 8, marginLeft: -4 }} />
        ),
        onClick: toogleUserStatus,
      });
    }

    if (canDeleteUser && (isDisabledUser() || showReInviteButton() )) {
      items.push({
        label: t("profile.form.delete"),
        icon: (
          <Icon
            name="delete"
            style={{ marginRight: 8, marginLeft: -4 }}
          />
        ),
        onClick: openUserDeleteModal,
      });
    }

    if (canRemoveFromProject) {
      if (items.length) {
        items.push({ type: "divider" });
      }

      items.push({
        label: t("profile.form.remove_from_project"),
        icon: (
          <Icon
            name="delete"
            style={{ marginRight: 8, marginLeft: -4 }}
          />
        ),
        onClick: removeFromCurrentProject,
      });
    }

    if (items.length) {
      headerMenu = { items };
    }
  }

  if (isOpenProfile && user) {
    return (
      <Drawer
        open={isOpenProfile}
        size={placement === "left" ? 600 : 800}
        onClose={close}
        placement={placement}
      >
        <DrawerHeader
          title={t("profile.title")}
          isEditing={isEdit}
          onEdit={canShowEditButton ? editToogle : undefined}
          onClose={close}
          onSave={updateUser}
          onCancel={editToogle}
          menu={headerMenu}
        />
        {isDataLoaded() ? null : (
          <Form key={formKey} form={form} onFieldsChange={onFieldsChange}>
            <Form.Item name="avatar" valuePropName="fileList">
              <FormAvatar
                onChange={onAvatarChange}
                value={user.avatarUrl}
                readonly={!canEditUser}
              />
            </Form.Item>
            <div style={{ paddingBottom: 24 }} />
            <Form.Item name="role" label={t("profile.form.role")}>
              <FormSelect
                options={
                  isSystemAdmin
                    ? roleOptions
                    : roleOptions.filter((role) => role.value !== "systemAdmin")
                }
                disabled={isEdit && isCurrentUser()}
                defaultValue={
                  roleOptions.find((role) => role.value === user.role)
                }
                onSelect={onRoleChange}
                readonly={!isEdit}
                getPopupContainer={trigger => trigger.parentNode}
              />
            </Form.Item>
            <Form.Item name="firstName" label={t("profile.form.first_name")}>
              <FormInput
                defaultValue={user.firstName}
                maxLength={1000}
                readonly={!canEditUser}
              />
            </Form.Item>
            <Form.Item name="lastName" label={t("profile.form.last_name")}>
              <FormInput
                defaultValue={user.lastName}
                maxLength={1000}
                readonly={!canEditUser}
              />
            </Form.Item>
            <Form.Item name="email" label={t("profile.form.email")}>
              <FormInput
                defaultValue={user.email}
                maxLength={1000}
                readonly
                disabled={isEdit}
              />
            </Form.Item>
            <Form.Item label={t("profile.form.password")}>
              <FormInputButton
                value={"• • • • • • • • • • •"}
                onClick={openPasswordModal}
                readonly={!canEditUser}
              />
            </Form.Item>

            <UserAccessForm
              scope={accessScope}
              orgRole={accessScope === "project" ? "user" : (userRole || user.role)}
              sitesFor={sitesFor}
              projectRows={projectRows}
              updateRow={updateProjectRow}
              roles={roles}
              projectRoleDropdownKey={projectRoleDropdownKey}
              setProjectRoleDropdownKey={setProjectRoleDropdownKey}
              getProjectTitle={getProjectTitle}
              projectModalOpen={projectModalOpen}
              setProjectModalOpen={setProjectModalOpen}
              selectedProjectIds={selectedProjectIds}
              onSelectProject={handleSelectProject}
              isEdit={isEdit}
              canEdit={canEditUser}
              project={accessScope === "project" ? currentProject : null}
              removeProjectRow={removeProjectRow}
              readOnly={!isEdit}
            />

            {!isEdit && (
              <>
                <FormHeader label={"Status"} />
                <Form.Item label={t("profile.form.status")}>
                  <FormUserStatus status={user.status} />
                </Form.Item>
                <Form.Item label={t("profile.form.invited")}>
                  <FormInput
                    value={getDate(user.invitedAt)}
                    readonly={!isEdit}
                    disabled={isEdit}
                  />
                </Form.Item>
                <Form.Item label={t("profile.form.last_active")}>
                  <FormInput
                    value={getDateByDiff(user.lastActivity)}
                    readonly={!isEdit}
                    disabled={isEdit}
                  />
                </Form.Item>
              </>
            )}
          </Form>
        )}
        <ChangePasswordForm
          t={t}
          isOpen={isOpenPassword}
          close={closePasswordModal}
        />

        <DeleteModalComponent
          isModalOpen={isOpenDeleteModal}
          handleCancel={closeDeleteUserModal}
          handleOk={deleteUser}
          item={user}
        />
      </Drawer>
    );
  }

  return null;
};

export default Profile;
